import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { Box } from '@mui/material';
import businessService from '../services/business.service';
import Swal from 'sweetalert2';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
    margin: 0;
    overflow: hidden;
  }
`;

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const CampaignName = styled.h1`
  font-family: 'Fredoka One', sans-serif;
  font-size: 2.1rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 3px white;
  margin-bottom: 1.8rem;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const Subtitle = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 1.2rem;
  color: #666;
  text-align: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const BusinessDashboard = () => {
  const { adminUrl } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [prizeStats, setPrizeStats] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demographicStats, setDemographicStats] = useState(null);

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        // Verificar token
        const token = localStorage.getItem('businessToken');
        if (!token) {
          navigate(`/business/${adminUrl}`);
          return;
        }

        const isValid = await businessService.verifyToken(token);
        if (!isValid) {
          localStorage.removeItem('businessToken');
          navigate(`/business/${adminUrl}`);
          return;
        }

        setIsAuthenticated(true);
        loadDashboardData();
      } catch (error) {
        console.error('Error de autenticación:', error);
        navigate(`/business/${adminUrl}`);
      }
    };

    initializeDashboard();
  }, [adminUrl, navigate]);

  const loadDashboardData = async () => {
    try {
      const [dashboardStats, prizes, winnersData] = await Promise.all([
        businessService.getDashboard(),
        businessService.getPrizeStats(),
        businessService.getWinners()
      ]);
      
      setStats(dashboardStats);
      setPrizeStats(prizes);
      setWinners(winnersData.winners);
      setLoading(false);

      const stats = await businessService.getStats();
      setDemographicStats(stats.stats);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setLoading(false);
    }
  };

  const handleRedeem = async (participationId) => {
    try {
      await businessService.redeemPrize(participationId);
      // Recargar los datos para actualizar la tabla
      loadDashboardData();
      Swal.fire({
        title: '¡Éxito!',
        text: 'Premio canjeado correctamente',
        icon: 'success'
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'No se pudo canjear el premio',
        icon: 'error'
      });
    }
  };

  const handleDownload = async () => {
    try {
      await businessService.downloadWinners();
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'No se pudo descargar el archivo',
        icon: 'error'
      });
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!stats || !prizeStats) {
    return (
      <>
        <GlobalStyle />
        <DashboardContainer>
          <CampaignName>Panel de Control</CampaignName>
          <Subtitle>Gestiona tu campaña de Rasca y Gana</Subtitle>
          <p>No hay datos disponibles</p>
        </DashboardContainer>
      </>
    );
  }

  const ageChartData = {
    labels: demographicStats?.ageDistribution.map(d => d.range) || [],
    datasets: [{
      data: demographicStats?.ageDistribution.map(d => d.count) || [],
      backgroundColor: [
        '#FF6384',
        '#36A2EB',
        '#FFCE56',
        '#4BC0C0',
        '#9966FF'
      ]
    }]
  };

  const genderChartData = {
    labels: demographicStats?.genderDistribution.map(d => d.gender) || [],
    datasets: [{
      data: demographicStats?.genderDistribution.map(d => d.count) || [],
      backgroundColor: [
        '#36A2EB',
        '#FF6384',
        '#FFCE56'
      ]
    }]
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: 'right'
      }
    }
  };

  return (
    <>
      <GlobalStyle />
      <DashboardContainer>
        <CampaignName>Panel de Control</CampaignName>

        <StatsGrid>
          <StatCard>
            <h3>Participaciones Hoy</h3>
            <p>{stats.today?.participations || 0}</p>
          </StatCard>
          <StatCard>
            <h3>Premios Entregados Hoy</h3>
            <p>{stats.today?.prizes || 0}</p>
          </StatCard>
          <StatCard>
            <h3>Total Participaciones</h3>
            <p>{stats.totalParticipations || 0}</p>
          </StatCard>
          <StatCard>
            <h3>Total Premios Entregados</h3>
            <p>{stats.totalPrizesGiven || 0}</p>
          </StatCard>
        </StatsGrid>

        <DemographicsSection>
          <h2>Datos Demográficos</h2>
          <DemographicsGrid>
            <ChartContainer>
              <h3>Distribución por Edad</h3>
              <Pie data={ageChartData} options={chartOptions} />
            </ChartContainer>
            <ChartContainer>
              <h3>Distribución por Género</h3>
              <Pie data={genderChartData} options={chartOptions} />
            </ChartContainer>
          </DemographicsGrid>
        </DemographicsSection>

        <PrizesSection>
          <h2>Estado de los Premios</h2>
          {prizeStats.length > 0 ? (
            <PrizesTable>
              <thead>
                <tr>
                  <th>Premio</th>
                  <th>Entregados</th>
                  <th>Canjeados</th>
                  <th>Disponibles</th>
                </tr>
              </thead>
              <tbody>
                {prizeStats.map(prize => (
                  <tr key={prize._id}>
                    <td>{prize.name}</td>
                    <td>{prize.participations}</td>
                    <td>{prize.claimed}</td>
                    <td>{prize.remaining}</td>
                  </tr>
                ))}
              </tbody>
            </PrizesTable>
          ) : (
            <p>No hay datos de premios disponibles</p>
          )}
        </PrizesSection>

        <WinnersSection>
          <HeaderBox>
            <h2>Ganadores</h2>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
            >
              Descargar CSV
            </Button>
          </HeaderBox>

          <WinnersTable>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Premio</th>
                <th>Código</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {winners.map((winner) => (
                <tr key={winner._id}>
                  <td>{format(new Date(winner.participationDate), 'dd/MM/yyyy HH:mm', { locale: es })}</td>
                  <td>{winner.prize.name}</td>
                  <td>
                    <PrizeCode>{winner.prize.redeemCode}</PrizeCode>
                  </td>
                  <td>
                    <PrizeStatus claimed={winner.prize.claimed}>
                      {winner.prize.claimed ? 'Canjeado' : 'Pendiente'}
                    </PrizeStatus>
                  </td>
                  <td>
                    {!winner.prize.claimed && (
                      <ActionButton
                        onClick={() => handleRedeem(winner._id)}
                      >
                        Canjear
                      </ActionButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </WinnersTable>

          {winners.length === 0 && (
            <EmptyMessage>No hay ganadores todavía</EmptyMessage>
          )}
        </WinnersSection>

        
      </DashboardContainer>
    </>
  );
};

// Estilos...
const DashboardContainer = styled.div`
  height: 100vh;
  overflow-y: auto;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  /* Para navegadores webkit (Chrome, Safari) */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  
  h3 {
    color: #666;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 2rem;
    font-weight: bold;
    color: #1a1a1a;
  }
`;

const PrizesSection = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`;

const PrizesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
  }
`;

const WinnersSection = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-top: 2rem;
`;

const WinnersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

const PrizeCode = styled.span`
  background: #f0f9ff;
  color: #0369a1;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-weight: 600;
`;

const PrizeStatus = styled.span`
  background: ${props => props.claimed ? '#dcfce7' : '#fff1f2'};
  color: ${props => props.claimed ? '#166534' : '#be123c'};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: #666;
  padding: 2rem;
`;

const ActionButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.2s;

  &:hover {
    background: #45a049;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const DemographicsSection = styled.section`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin: 2rem 0;
`;

const DemographicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartContainer = styled.div`
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  h3 {
    text-align: center;
    margin-bottom: 1rem;
    color: #333;
  }
`;

export default BusinessDashboard;